import React, { useState } from 'react';
import { translateLeaveStatus } from '../../../../../utils';
import { Leave, AbsencePeriodDecisions } from '../../../../../data-models';
import dayjs from 'dayjs';
import { dateFormat } from './shared-styles';
import PagerSection from '../../../../base/pager-section';
import { PageChangeEvent } from '@progress/kendo-react-data-tools';

import {
  ExpandableCard,
  ExpandableCardHeader,
  ExpandedContent,
  ArrowWithDescription,
  TextBase,
  HeaderTextMedium,
  StatusIndicator,
  MobileContainerSM,
  DesktopContainerSM,
} from '../../../tw-components';
import { isEmptyObject } from '../../tabs/payments';

export function mergeConsolidatedPeriods(
  absencePeriodDecisions: AbsencePeriodDecisions[],
): AbsencePeriodDecisions[] {
  const map: Record<string, AbsencePeriodDecisions> = {};

  // Helper function to normalize absenceId from NTN-XXXXX-XXX-01 to NTN-XXXXX
  // since we are merging NTN-XXXXX-ABS-01 and NTN-XXXXX-ABS-02
  const normalizeAbsenceId = (id: string): string => {
    const regex = /^(NTN-\d+)/;
    const match = regex.exec(id);
    return match ? match[1] : id;
  };

  for (const item of absencePeriodDecisions) {
    const key = item.leavePlanName;

    if (!map[key]) {
      // Initialize
      map[key] = {
        leavePlanName: item.leavePlanName,
        absenceId: normalizeAbsenceId(item.absenceId),
        consolidatedPeriods: [],
      };
    }

    if (Array.isArray(item.consolidatedPeriods)) {
      // Push periods while injecting the relevant absenceId into each period
      map[key].consolidatedPeriods.push(
        ...item.consolidatedPeriods.map((p) => ({
          ...p,
          absenceId: (p as any).absenceId ?? item.absenceId,
        })),
      );
    }
  }

  return Object.values(map);
}

const LeaveCardItems: (props: {
  leaves: Array<Leave>;
  absencePeriodDecisions: Array<AbsencePeriodDecisions>;
}) => React.ReactElement = (props) => {
  const { absencePeriodDecisions } = props;
  const mergedAbsencePeriods = mergeConsolidatedPeriods(absencePeriodDecisions);
  const sortedConsolidatedPeriods = [...mergedAbsencePeriods].sort((a, b) => {
    return (
      (b.leavePlanName === 'Federal FMLA' ? 1 : 0) -
      (a.leavePlanName === 'Federal FMLA' ? 1 : 0)
    );
  });

  return (
    <>
      {sortedConsolidatedPeriods.map(
        (absencePeriodDecision: AbsencePeriodDecisions, i: number) => (
          <LeaveCardItem
            absencePeriodDecision={absencePeriodDecision}
            id={`${absencePeriodDecision.absenceId}-item-${i}`}
            key={`${absencePeriodDecision.absenceId}-item-${i}`}
          />
        ),
      )}
    </>
  );
};

export const LeaveCardItem: (props: {
  absencePeriodDecision: AbsencePeriodDecisions;
  id: string;
}) => React.ReactElement = (props) => {
  const { absencePeriodDecision } = props;
  const [showDetails, setShowDetails] = React.useState(false);

  const [page, setPage] = useState({
    skip: 0,
    take: 6,
  });

  const handlePageChange = (e: PageChangeEvent) => {
    setPage({
      skip: e.skip,
      take: e.take,
    });
  };

  const { skip, take } = page; //Used for Pagination

  const paidCategory =
    absencePeriodDecision.consolidatedPeriods[0].leavePlanCategory;

  let mappedPaidBy;

  if (paidCategory === 'Paid') {
    mappedPaidBy = 'Yes';
  } else if (
    paidCategory === 'Admin or ER paid' ||
    paidCategory === 'Admin ER-Paid'
  ) {
    mappedPaidBy = 'By Employer';
  } else if (paidCategory === 'Non-Paid') {
    mappedPaidBy = 'No';
  } else {
    mappedPaidBy = '';
  }

  return (
    <ExpandableCard>
      <ExpandableCardHeader
        id={props.id}
        onClick={() => setShowDetails(!showDetails)}
      >
        {/* Mobile View */}
        <MobileContainerSM>
          <div className="tw-flex tw-flex-col tw-w-full tw-gap-2 tw-justify-between tw-items-center">
            <div className="tw-flex tw-flex-row tw-w-full tw-gap-2">
              <HeaderTextMedium>Plan Name:</HeaderTextMedium>
              <TextBase>{absencePeriodDecision.leavePlanName}</TextBase>
            </div>
            <div className="tw-flex tw-w-full tw-gap-2 tw-items-center">
              <div className="tw-flex tw-flex-row tw-w-1/2 tw-gap-2 tw-items-center">
                {mappedPaidBy ? (
                  <>
                    <HeaderTextMedium>Payable if approved?</HeaderTextMedium>
                    <TextBase>{mappedPaidBy}</TextBase>
                  </>
                ) : null}
              </div>
              <div className="tw-flex tw-flex-row tw-w-1/2 tw-justify-end">
                <ArrowWithDescription rotate={`${showDetails}`} />
              </div>
            </div>
          </div>
        </MobileContainerSM>

        {/* Desktop View */}
        <DesktopContainerSM className="tw-justify-between tw-items-center">
          <div className="tw-flex tw-flex-row tw-w-3/4 tw-gap-2">
            <div className="tw-flex tw-flex-col tw-w-2/3 tw-gap-2">
              <HeaderTextMedium>Plan Name</HeaderTextMedium>
              <TextBase>{absencePeriodDecision.leavePlanName}</TextBase>
            </div>
            <div className="tw-flex tw-flex-col tw-w-1/3 tw-gap-2">
              <HeaderTextMedium>Payable if approved?</HeaderTextMedium>
              {mappedPaidBy}
            </div>
          </div>
          <div className="tw-w-1/4">
            <ArrowWithDescription rotate={`${showDetails}`} />
          </div>
        </DesktopContainerSM>
      </ExpandableCardHeader>
      {showDetails && (
        <ExpandedContent>
          <div
            className="tw-flex tw-flex-row tw-gap-3 tw-w-full tw-mb-2 tw-px-3 tw-py-2 tw-bg-unm-adhoc3
                          tw-border-b tw-border-solid tw-border-unm-interactive01-400 tw-border-0  last:tw-border-b-0 tw-items-center"
          >
            {/* Mobile View */}
            <MobileContainerSM className="tw-items-center">
              <HeaderTextMedium className="tw-w-1/3 tw-justify-center">
                Period
              </HeaderTextMedium>
              <HeaderTextMedium className="tw-flex tw-justify-center tw-w-1/3">
                Leave Schedule
              </HeaderTextMedium>
              <HeaderTextMedium className="tw-w-1/3 tw-text-center">
                Status
              </HeaderTextMedium>
            </MobileContainerSM>

            {/* Desktop View */}
            <DesktopContainerSM>
              <HeaderTextMedium className="tw-w-3/12">
                Period Begins
              </HeaderTextMedium>
              <HeaderTextMedium className="tw-w-3/12">
                Period Ends
              </HeaderTextMedium>
              <HeaderTextMedium className="tw-w-3/12">
                Leave Schedule
              </HeaderTextMedium>
              <HeaderTextMedium className="tw-w-3/12 tw-text-center">
                Status
              </HeaderTextMedium>
            </DesktopContainerSM>
          </div>

          {isEmptyObject(absencePeriodDecision.consolidatedPeriods) ? (
            <div>Details unavailable</div>
          ) : (
            <>
              {[...(absencePeriodDecision.consolidatedPeriods || [])]
                .sort(
                  (a, b) =>
                    new Date(a.startDate).getTime() -
                    new Date(b.startDate).getTime(),
                )

                .slice(skip, skip + take)
                .map((consolidatedPeriod, index) => {
                  const {
                    leaveRequestId,
                    timeDecisionStatus,
                    startDate,
                    endDate,
                    absencePeriodType,
                    adjudicationStatus,
                    decisionStatus,
                  } = consolidatedPeriod;

                  if (!leaveRequestId || !timeDecisionStatus) return null;

                  let mappedTimeDecisionStatus: string;

                  if (
                    adjudicationStatus === 'Accepted' &&
                    decisionStatus === 'Pending'
                  ) {
                    mappedTimeDecisionStatus = 'Pending';
                  } else if (timeDecisionStatus.includes('Pending')) {
                    mappedTimeDecisionStatus = 'Pending';
                  } else {
                    mappedTimeDecisionStatus = timeDecisionStatus;
                  }

                  const statusConfig = translateLeaveStatus(
                    mappedTimeDecisionStatus,
                  );
                  return (
                    <div
                      key={`${leaveRequestId}-${index}`}
                      className="tw-flex tw-flex-row tw-items-center tw-gap-3 tw-px-3 tw-w-full tw-mb-3 last:tw-mb-0
                          tw-border-b tw-border-solid tw-border-unm-interactive01-400 tw-border-0 tw-pb-2 last:tw-pb-0 last:tw-border-b-0"
                    >
                      {/* Mobile View */}
                      <MobileContainerSM className="tw-items-center">
                        <TextBase className="tw-w-1/3">
                          {dayjs(startDate).isValid() ? (
                            <div>{dayjs(startDate).format(dateFormat)}</div>
                          ) : null}
                          {dayjs(endDate).isValid() ? (
                            <div>{dayjs(endDate).format(dateFormat)}</div>
                          ) : null}
                        </TextBase>
                        <TextBase className="tw-flex tw-justify-center tw-w-1/3">
                          {absencePeriodType === 'Blackout Period'
                            ? 'N/A'
                            : absencePeriodType}
                        </TextBase>
                        <div className="tw-w-1/3">
                          <StatusIndicator
                            background={statusConfig.backgroundColor}
                            color={statusConfig.color}
                          >
                            {mappedTimeDecisionStatus}
                          </StatusIndicator>
                        </div>
                      </MobileContainerSM>
                      <DesktopContainerSM>
                        <TextBase className="tw-w-3/12">
                          {dayjs(startDate).isValid() ? (
                            <div>{dayjs(startDate).format(dateFormat)}</div>
                          ) : null}
                        </TextBase>
                        <TextBase className="tw-w-3/12">
                          {dayjs(endDate).isValid() ? (
                            <div>{dayjs(endDate).format(dateFormat)}</div>
                          ) : null}
                        </TextBase>
                        <TextBase className="tw-w-3/12">
                          {absencePeriodType === 'Blackout Period'
                            ? 'N/A'
                            : absencePeriodType}
                        </TextBase>
                        <div className="tw-w-3/12">
                          <StatusIndicator
                            background={statusConfig.backgroundColor}
                            color={statusConfig.color}
                          >
                            {mappedTimeDecisionStatus}
                          </StatusIndicator>
                        </div>
                      </DesktopContainerSM>
                    </div>
                  );
                })}
              <div className="tw-flex-grow"></div>
              <PagerSection
                skip={skip}
                take={take}
                handlePageChange={handlePageChange}
                elements={absencePeriodDecision.consolidatedPeriods}
              />
            </>
          )}
        </ExpandedContent>
      )}
    </ExpandableCard>
  );
};

export default LeaveCardItems;
